import axios from 'axios';
import { attachTokenRefresh } from '@/utils/authInterceptors';

const baseURL = process.env.VUE_APP_ENV === 'production'
  ? 'https://api.vibrant-america.com/v2/portal/trans-service'
  : 'https://api.vibrant-america.com/v2/portal/trans-service-st';

const vitalsApi = axios.create({ baseURL });

vitalsApi.interceptors.request.use(
  function (config) {
    let token = sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

attachTokenRefresh(vitalsApi);

/**
 * Create a vital entry for a patient
 * POST /vitals/createVitalEntry
 */
export const createVitalEntry = async ({ patient_id, vital_type, value, unit, source = 'manual', notes = '', timestamp }) => {
  const payload = { patient_id, vital_type, value: String(value), unit, source, notes };
  if (timestamp) payload.timestamp = timestamp;
  const response = await vitalsApi.post('/vitals/createVitalEntry', payload);
  return response.data;
};

/**
 * Get the most recent vitals for a patient
 * GET /vitals/getRecentVitals?patient_id=X
 */
export const getRecentVitals = async (patientId) => {
  const response = await vitalsApi.get('/vitals/getRecentVitals', {
    params: { patient_id: patientId }
  });
  return response.data;
};

/**
 * Transform API entries array into flat health info object
 * for HealthInfoCard display
 */
export const transformVitalsToHealthInfo = (entries) => {
  if (!entries || !Array.isArray(entries)) return null;

  const info = {
    height: null,
    weight: null,
    bmi: null,
    heartRate: null,
    respiratoryRate: null,
    temperature: null,
    systolic: null,
    diastolic: null,
    oxygenSaturation: null,
    painLevel: null,
    causeOfPain: null
  };

  for (const entry of entries) {
    switch (entry.vital_type) {
      case 'Ht':
        info.height = entry.value;
        break;
      case 'Wt':
        info.weight = entry.value;
        break;
      case 'BMI':
        info.bmi = entry.value;
        break;
      case 'HR':
        info.heartRate = entry.value;
        break;
      case 'RR':
        info.respiratoryRate = entry.value;
        break;
      case 'Temp':
        info.temperature = entry.value;
        break;
      case 'BP': {
        const parts = entry.value ? entry.value.split('/') : [];
        if (parts.length === 2) {
          info.systolic = parts[0];
          info.diastolic = parts[1];
        }
        break;
      }
      case 'SpO2':
        info.oxygenSaturation = entry.value;
        break;
      case 'Pain':
        info.painLevel = entry.value;
        info.causeOfPain = entry.notes || null;
        break;
    }
  }

  // Return null if all values are still null (no vitals found)
  const hasAnyValue = Object.values(info).some(v => v !== null);
  return hasAnyValue ? info : null;
};
